import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import {
  FeatherArrowLeft,
  FeatherCheckCircle,
  FeatherDownload,
  FeatherUserPlus,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { AcknowledgementTrackingPanel } from "@/components/acknowledgements/AcknowledgementTrackingPanel";
import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { parseFieldSchema } from "@/components/event-records/event-record-schema";
import { deriveTimingRows } from "@/components/event-records/fire-drill";
import { LinkedFollowUpActions } from "@/components/event-records/LinkedFollowUpActions";
import { RequestAcknowledgementDialog } from "@/components/event-records/RequestAcknowledgementDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import {
  getEventRecordPdfQueryControllerGet,
  getGetEventRecordQueryControllerGetQueryKey,
  useAcknowledgeEventRecordCommandControllerAcknowledge,
  useGetEventRecordQueryControllerGet,
} from "@/generated/event-records/event-records";
import { useListNurseryEventTypeConfigsQueryControllerList } from "@/generated/event-type-configs/event-type-configs";
import type { EventRecordUserSummaryDto } from "@/generated/model";
import { invalidateAcknowledgementQueries } from "@/lib/acknowledgements/invalidate-acknowledgement-queries";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import { downloadUrl } from "@/lib/download";
import { describeAcknowledgerRoles } from "@/lib/event-records/acknowledgement-rules";
import { recordAcknowledgementState } from "@/lib/event-records/acknowledgement-state";
import { formatFieldValue } from "@/lib/event-records/format-field-value";
import { canReportEvents } from "@/lib/roles";
import { eventRecordPill } from "@/lib/workspace/status-pill";

export const Route = createFileRoute(
  "/_authenticated/event-records/$eventRecordId",
)({
  component: EventRecordDetailPage,
});

function EventRecordDetailPage() {
  const { eventRecordId } = useParams({
    from: "/_authenticated/event-records/$eventRecordId",
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { nurseryRole } = useNursery();
  const { isPlatformAdmin, user } = useAuth();
  const { isOrgAdmin } = useOrganisation();
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);

  const { data, isLoading, error } = useGetEventRecordQueryControllerGet(
    eventRecordId,
    // Refetch on mount so a corrective task added via the create flow shows on
    // return to this page.
    { query: { refetchOnMount: "always" } },
  );
  const record = data?.data;

  const acknowledgeMutation =
    useAcknowledgeEventRecordCommandControllerAcknowledge();

  // Who this nursery says must acknowledge THIS event type. Not on the record
  // payload — the record only reports what the current viewer may do, which
  // cannot answer "who is it waiting for". Readable by any nursery member, and
  // the rules change rarely, so the shared cache usually answers it.
  const { data: eventTypeConfigs } =
    useListNurseryEventTypeConfigsQueryControllerList(record?.nurseryId ?? "", {
      query: { enabled: Boolean(record?.nurseryId) },
    });

  const canCreateTasks = canReportEvents({
    isPlatformAdmin,
    isOrgAdmin,
    nurseryRole,
  });

  const goToCreateTask = () => {
    if (!record) return;
    navigate({
      to: "/follow-up-actions/create",
      search: {
        eventRecordId: record.id,
        eventRecordName: record.eventTypeName,
        nurseryId: record.nurseryId,
        scheduledQuestionnaireId: "",
        answerId: "",
        questionText: "",
        checklistName: "",
      },
    });
  };

  const handleBack = () => {
    if (window.history.length > 1) window.history.back();
    // view=today is required for the tab strip to render at all — without it
    // the dashboard falls back to the overview and the tab is ignored.
    else
      navigate({ to: "/dashboard", search: { view: "today", tab: "events" } });
  };

  /**
   * Acknowledging moves the record between Events Hub tabs, and each tab is its
   * own query key carrying its own copy of the count badges — so refreshing only
   * this record would leave the Hub showing pre-acknowledgement rows and counts
   * for the app's five-minute staleTime. Invalidate the whole event-record family
   * (this record, every Hub tab, the dashboard Events tab) rather than just self.
   *
   * The acknowledgement helper, not the event-record one: every caller of this is
   * an acknowledgement write. Requesting one publishes an item, and answering one
   * signs a request in the shared context — both of which change the tracking
   * panel mounted on this very page. The event-record helper does not reach
   * `/acknowledgements`, so the panel kept serving its pre-write roster; this one
   * is a superset of it and covers both families.
   */
  const refetchDetail = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetEventRecordQueryControllerGetQueryKey(eventRecordId),
      }),
      invalidateAcknowledgementQueries(queryClient),
    ]);

  const handleAcknowledge = async () => {
    try {
      const response = await acknowledgeMutation.mutateAsync({ eventRecordId });
      // One endpoint, two outcomes (NAU-342): a configured acknowledger closes the
      // record; anyone else is only answering a request made of them, and must not
      // be told they signed it off.
      toast.success(
        response?.data?.closedRecord
          ? "Record acknowledged"
          : "Thanks — your response has been recorded",
      );
      await refetchDetail();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      // 409 covers more than a double-tap — the server also refuses when the
      // record has moved on (already resolved, say) — so surface its own message
      // rather than asserting it was already acknowledged.
      if (status === 409) {
        toast.info(
          extractApiErrorMessage(
            err,
            "This record has already been dealt with",
          ),
        );
        await refetchDetail();
        return;
      }
      toast.error(
        extractApiErrorMessage(err, "Failed to acknowledge — please try again"),
      );
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await getEventRecordPdfQueryControllerGet(eventRecordId);
      const url = (res?.data as { url?: string } | undefined)?.url;
      if (!url) throw new Error("No PDF URL returned");
      downloadUrl(url);
      toast.success("PDF ready — check your downloads");
    } catch {
      toast.error("Failed to generate PDF — please try again");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="flex w-full flex-col items-start gap-4">
        <IconButton icon={<FeatherArrowLeft />} onClick={handleBack} />
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 bg-white p-6">
          <span className="text-body font-body text-default-font">
            Record not found or you don&apos;t have access.
          </span>
        </div>
      </div>
    );
  }

  const schema = parseFieldSchema(record.eventTypeFieldSchema);
  const fieldData = record.data as Record<string, unknown>;
  const derivedRows = deriveTimingRows(schema, fieldData);
  const acknowledgement = record.acknowledgement ?? null;
  // What this caller may do, resolved server-side against the nursery's own
  // acknowledgement rules — the client no longer guesses from role alone.
  const capabilities = record.capabilities ?? null;
  const acknowledgementRequests = record.acknowledgementRequests ?? [];
  const acknowledgementState = recordAcknowledgementState(
    record.status,
    acknowledgement !== null,
  );
  const acknowledgerRoles = describeAcknowledgerRoles(
    eventTypeConfigs?.data.data.find(
      (config) => config.eventTypeId === record.eventTypeId,
    )?.acknowledgerRoles,
  );
  const outstandingRequestCount = acknowledgementRequests.filter(
    (request) => request.respondedAt === null,
  ).length;

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1 pb-6">
        <div className="flex w-full flex-wrap items-center gap-2">
          <IconButton icon={<FeatherArrowLeft />} onClick={handleBack} />
          <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
            {record.eventTypeName}
          </span>
          {/* The same pill the Events Hub renders. Until NAU-342 this page derived
              a two-state badge from `acknowledgement != null`, which showed amber
              where the Hub showed red and green "Acknowledged" where the Hub showed
              amber "In progress" — and could not express Archived at all. */}
          <WorkspaceStatusBadge pill={eventRecordPill(record.status)} />
          {outstandingRequestCount > 0 && (
            <Badge variant="neutral">
              {outstandingRequestCount === 1
                ? "1 acknowledgement requested"
                : `${outstandingRequestCount} acknowledgements requested`}
            </Badge>
          )}
        </div>
        <span className="text-body font-body text-subtext-color">
          Submitted {format(new Date(record.submittedAt), "PPp")}
          {record.createdBy && <> by {userLabel(record.createdBy)}</>}
        </span>
      </div>

      <div className="flex w-full flex-col items-start gap-6 pb-8">
        <div className="flex w-full max-w-3xl mx-auto flex-col items-start gap-6">
          {/* Action bar */}
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <Button
              className="h-10"
              variant="neutral-secondary"
              size="medium"
              icon={<FeatherDownload />}
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? "Generating PDF…" : "Download PDF"}
            </Button>
            {capabilities?.canRequestAcknowledgement && (
              <Button
                className="h-10"
                variant="neutral-secondary"
                size="medium"
                icon={<FeatherUserPlus />}
                onClick={() => setRequestDialogOpen(true)}
              >
                Request acknowledgement
              </Button>
            )}
            {capabilities?.canAcknowledge && (
              <Button
                className="h-10"
                variant="brand-primary"
                size="medium"
                icon={<FeatherCheckCircle />}
                onClick={handleAcknowledge}
                disabled={acknowledgeMutation.isPending}
              >
                {acknowledgeMutation.isPending
                  ? "Acknowledging…"
                  : "Acknowledge"}
              </Button>
            )}
            {/* Answering a request made of you personally. Deliberately worded so it
              cannot be mistaken for signing the record off — it records that you
              have read it and changes nothing else (AC 6). */}
            {!capabilities?.canAcknowledge &&
              capabilities?.canRespondToRequest && (
                <Button
                  className="h-10"
                  variant="brand-primary"
                  size="medium"
                  icon={<FeatherCheckCircle />}
                  onClick={handleAcknowledge}
                  disabled={acknowledgeMutation.isPending}
                >
                  {acknowledgeMutation.isPending
                    ? "Confirming…"
                    : "Confirm I have read this"}
                </Button>
              )}
          </div>

          {/* Structured fields, rendered in schema order */}
          <section className="flex w-full flex-col items-start gap-3 rounded-md border border-neutral-200 p-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Record details
            </span>
            <dl className="grid w-full grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {schema.fields.map((field) => (
                <div key={field.key} className="flex flex-col gap-0.5">
                  <dt className="text-caption font-caption text-subtext-color">
                    {field.label}
                  </dt>
                  <dd className="text-body font-body text-default-font whitespace-pre-wrap">
                    {formatFieldValue(field, fieldData[field.key])}
                  </dd>
                </div>
              ))}
              {/* Calculated, display-only timings (never stored). */}
              {derivedRows.map((row) => (
                <div key={row.key} className="flex flex-col gap-0.5">
                  <dt className="text-caption font-caption text-subtext-color">
                    {row.label}
                  </dt>
                  <dd className="text-body font-body text-default-font">
                    {row.value ?? "—"}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          {/* The record's own sign-off, as distinct from the read-and-sign
            requests below. It used to appear only once acknowledged: a manager
            looking at an unacknowledged record saw nothing here and a full
            tracking panel underneath, so the requests — which close nothing —
            looked like the thing the record was waiting for. Saying "not yet
            acknowledged, it needs X" is the whole point of the section.

            Always rendered, in one of four states. "Not acknowledged" on its own
            is ambiguous — it covers waiting, never needed, and closed without —
            and showing the wrong one of those is worse than showing nothing. */}
          {acknowledgementState === "ACKNOWLEDGED" && acknowledgement ? (
            <section className="flex w-full flex-col items-start gap-2 rounded-md border border-success-200 bg-success-50 p-4">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Record acknowledged
              </span>
              <span className="text-body font-body text-default-font">
                Acknowledged by{" "}
                {acknowledgement.acknowledgedBy
                  ? userLabel(acknowledgement.acknowledgedBy)
                  : "a manager"}{" "}
                · {format(new Date(acknowledgement.acknowledgedAt), "PPp")}
              </span>
              <span className="text-caption font-caption text-subtext-color">
                This is the acknowledgement that signs the record off.
              </span>
            </section>
          ) : acknowledgementState === "AWAITING" ? (
            <section className="flex w-full flex-col items-start gap-2 rounded-md border border-warning-200 bg-warning-50 p-4">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Not yet acknowledged
              </span>
              <span className="text-body font-body text-default-font">
                {acknowledgerRoles
                  ? `This record needs ${acknowledgerRoles} to acknowledge it.`
                  : "This record has not been acknowledged yet."}
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Asking someone to read and sign it does not acknowledge it.
              </span>
            </section>
          ) : acknowledgementState === "NOT_REQUIRED" ? (
            <section className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-4">
              <span className="text-heading-3 font-heading-3 text-default-font">
                No acknowledgement needed
              </span>
              {/* Names the person rather than their role, and says "sign it off"
                rather than naming today's rules. The skip was decided by the
                RESOLVER list as it stood when the record was filed, so quoting
                the nursery's current configuration could describe a rule that
                did not apply to this record. */}
              <span className="text-body font-body text-default-font">
                {record.createdBy
                  ? userLabel(record.createdBy)
                  : "Whoever filed"}{" "}
                filed this record and could sign records of this type off, so it
                did not need a separate acknowledgement.
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Records filed by anyone else wait to be acknowledged before they
                move on.
              </span>
            </section>
          ) : (
            <section className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-4">
              <span className="text-heading-3 font-heading-3 text-default-font">
                No acknowledgement recorded
              </span>
              <span className="text-body font-body text-default-font">
                This record was closed without a separate acknowledgement.
              </span>
            </section>
          )}
        </div>

        {/* Ad-hoc acknowledgement requests (NAU-342). Separate from the panel above
            because these responses record that someone read the record — they never
            close it, so presenting them as acknowledgements would mislead. */}
        {/*
          The manager's acknowledgement tracking (NAU-410 AC24-AC27). Replaces
          the flat "Acknowledgement requested" list this page used to show: it
          reports Read and Signed separately, adds the completion tiles, the
          last-reminded date, per-person and send-to-all reminders and the CSV
          export. Self-contained, so Risk Hub and Policy Hub can mount the same
          component when they land.

          Renders nothing when the record was never sent out, which is the
          normal case for most records.

          Outside the narrow column: a data table needs the width, the record
          form above does not.
        */}
        <AcknowledgementTrackingPanel
          nurseryId={record.nurseryId}
          sourceId={record.id}
          itemType="EVENT_RECORD"
          canSendReminders={Boolean(capabilities?.canRequestAcknowledgement)}
          // Named for what it is on an incident. "Acknowledgements" sat
          // directly under a section of almost the same name meaning something
          // else entirely, and the reader page these people see already says
          // "read and sign" — so this is the vocabulary they arrive with.
          title="Asked to read and sign"
          description="People asked to read this record and confirm it. Their confirmation is recorded permanently, but it does not acknowledge the record."
        />

        <div className="flex w-full max-w-3xl mx-auto flex-col items-start gap-6">
          {/* Corrective follow-up tasks (NAU-221) */}
          <LinkedFollowUpActions
            actions={record.followUpActions}
            canCreate={canCreateTasks}
            onCreate={goToCreateTask}
          />
        </div>
      </div>

      <RequestAcknowledgementDialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
        eventRecordId={eventRecordId}
        nurseryId={record.nurseryId}
        currentUserId={user?.id ?? ""}
        alreadyRequestedUserIds={acknowledgementRequests.map(
          (request) => request.requestedUserId,
        )}
        onRequested={refetchDetail}
      />
    </div>
  );
}

/** Human-readable rendering of a stored field value by its declared type. */
function userLabel(u: EventRecordUserSummaryDto): string {
  return [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email;
}
